import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatGateway, { ActiveExportPayload } from '../../api/gateways/ChatGateway';
import { useUser } from '../../store/auth/AuthSelectors';
import { screens } from '../Navigation.types';
import { ChatMessage, ChatResponsePayload } from './ChatBot.types';
import { chatUiStrings, inferChatUiLocale } from './chatUiLocale';

// Simple ID generator
const generateId = () =>
	`${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

export function useChatBot(sessionIdFromUrl?: string) {
	const user = useUser();
	const navigate = useNavigate();
	const [sessionId, setSessionId] = useState<string | null>(null);
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [isTyping, setIsTyping] = useState(false);
	const [progressMessage, setProgressMessage] = useState('');
	const [progressPercent, setProgressPercent] = useState<number | null>(null);
	const [exportId, setExportId] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [isConnected, setIsConnected] = useState(false);
	const [tableReportId, setShowTableReport] = useState<string>('');
	const [elapsedSeconds, setElapsedSeconds] = useState(0);
	const [isFraudControlUploading, setIsFraudControlUploading] = useState(false);
	const sessionIdRef = useRef<string | null>(null);
	const messagesRef = useRef<ChatMessage[]>([]);
	const elapsedIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

	useEffect(() => {
		messagesRef.current = messages;
	}, [messages]);

	// fromTimestamp: epoch ms — pass when resuming after navigation
	const startElapsedTimer = useCallback((fromTimestamp?: number) => {
		const startMs = fromTimestamp ?? Date.now();
		sessionStorage.setItem('chat_elapsed_start', String(startMs));
		// Snap initial value to nearest 5-second boundary so resume looks right
		const initial = Math.floor((Date.now() - startMs) / 5000) * 5;
		setElapsedSeconds(initial);
		if (elapsedIntervalRef.current) clearInterval(elapsedIntervalRef.current);
		elapsedIntervalRef.current = setInterval(
			() => setElapsedSeconds((s) => s + 5),
			5000
		);
	}, []);

	// Full stop — clears sessionStorage so the timer won't resume on next mount
	const stopElapsedTimer = useCallback(() => {
		sessionStorage.removeItem('chat_elapsed_start');
		if (elapsedIntervalRef.current) {
			clearInterval(elapsedIntervalRef.current);
			elapsedIntervalRef.current = null;
		}
		setElapsedSeconds(0);
	}, []);

	const applyActiveExportFromResponse = useCallback(
		(active: ActiveExportPayload | null | undefined) => {
			if (!active?.exportId) {
				setProgressMessage('');
				setProgressPercent(null);
				setExportId('');
				return;
			}
			const loc = inferChatUiLocale(messagesRef.current);
			const fallback = chatUiStrings[loc].exportInProgressFallback;
			setExportId(active.exportId);
			setProgressMessage(
				active.progressMessage?.trim()
					? active.progressMessage
					: fallback
			);
			setProgressPercent(
				typeof active.progressPercent === 'number' &&
					!Number.isNaN(active.progressPercent)
					? Math.min(100, Math.max(0, active.progressPercent))
					: null
			);
		},
		[]
	);

	useEffect(() => {
		sessionIdRef.current = sessionId;
	}, [sessionId]);

	const clearConversation = useCallback(() => {
		stopElapsedTimer();
		setMessages([]);
		setSessionId(null);
		sessionIdRef.current = null;
		setProgressMessage('');
		setProgressPercent(null);
		setExportId('');
		localStorage.removeItem('chat_session_id');
		// Navigate to base chatbot route without sessionId
		navigate(screens.chatbot, { replace: true });
	}, [navigate, stopElapsedTimer]);

	// Initialize session - use sessionId from URL if provided, otherwise from localStorage or start with null
	useEffect(() => {
		const loadSessionMessages = async (sessionId: string) => {
			try {
				const data = await ChatGateway.api.getSessionMessages(sessionId);
				// Convert backend messages to ChatMessage format
				const chatMessages: ChatMessage[] = data.messages
					.filter((msg: any) => msg.role === 'user' || msg.role === 'assistant')
					.map((msg: any) => ({
						id: msg.id,
						role: msg.role,
						content: msg.content,
						timestamp: new Date(
							new Date(msg.createdAt).getTime() + 60 * 60 * 1000
						),
					}));

				messagesRef.current = chatMessages;
				setMessages(chatMessages);
				applyActiveExportFromResponse(data.activeExport ?? null);
				// Resume elapsed timer if processing was in flight when user navigated away
				if (data.activeExport?.exportId) {
					const storedStart = sessionStorage.getItem('chat_elapsed_start');
					if (storedStart) startElapsedTimer(parseInt(storedStart, 10));
				}
			} catch (error: any) {
				if (error?.statusCode === 401) {
					clearConversation();
					return;
				}
				setError('Unknown error. Redirecting to home.');
				clearConversation();
			}
		};

		// Priority: URL param > localStorage > null
		if (sessionIdFromUrl) {
			sessionIdRef.current = sessionIdFromUrl;
			setSessionId(sessionIdFromUrl);
			loadSessionMessages(sessionIdFromUrl);
		} else {
			const storedSessionId = localStorage.getItem('chat_session_id');
			// Only use stored session if it looks like a UUID (from backend)
			if (
				storedSessionId &&
				storedSessionId.includes('-') &&
				storedSessionId.length > 30
			) {
				sessionIdRef.current = storedSessionId;
				setSessionId(storedSessionId);
				loadSessionMessages(storedSessionId);
			} else {
				sessionIdRef.current = null;
				setSessionId(null);
			}
		}
	}, [user?.token, sessionIdFromUrl, clearConversation, applyActiveExportFromResponse, startElapsedTimer]);

	// Connect socket (allow sessionId to be null for first message)
	useEffect(() => {
		if (!user?.token) return;

		ChatGateway.socket.connect(user.token);

		// Set up listeners
		const handleConnect = () => {
			console.log('Connected to chat service');
			setIsConnected(true);
			setError(null);
			const sid = sessionIdRef.current;
			if (sid && user?.token) {
				ChatGateway.socket.subscribeSession(sid, user.token);
			}
		};

		const handleDisconnect = () => {
			console.log('Disconnected from chat service');
			setIsConnected(false);
		};

		const handleResponse = (data: ChatResponsePayload) => {
			stopElapsedTimer();
			setIsTyping(false);
			setProgressMessage('');
			setProgressPercent(null);

			// Store session ID from backend response and navigate to session URL
			if (data.sessionId) {
				setSessionId(data.sessionId);
				localStorage.setItem('chat_session_id', data.sessionId);
				// Navigate to session URL if not already there
				const currentPath = window.location.pathname;
				const sessionPath = `/chatbot/sessionId/${data.sessionId}`;
				if (currentPath !== sessionPath && !currentPath.includes(sessionPath)) {
					navigate(sessionPath, { replace: true });
				}
			}

			const assistantMessage: ChatMessage = {
				id: generateId(),
				role: 'assistant',
				content: data.message,
				timestamp: new Date(),
				toolsCalled: data.toolsCalled,
				tokensUsed: data.tokensUsed,
			};
			setMessages((prev) => [...prev, assistantMessage]);
			setError(null);
		};

		const handleProgress = (data: {
			message?: string;
			status: string;
			exportId?: string;
			sessionId?: string;
			percentage?: number;
			totalCount?: number;
			currentPage?: number;
			totalPages?: number;
		}) => {
			const sid = sessionIdRef.current;
			if (data.sessionId && sid && data.sessionId !== sid) {
				return;
			}
			if (data.status !== 'complete') {
				setIsTyping(false);
				if (data.message != null && data.message !== '') {
					setProgressMessage(data.message);
				}
				if (data.exportId) {
					setExportId(data.exportId);
				}
				if (typeof data.percentage === 'number' && !Number.isNaN(data.percentage)) {
					setProgressPercent(Math.min(100, Math.max(0, data.percentage)));
				} else if (data.status === 'starting' && data.totalCount != null) {
					setProgressPercent(null);
				}
			} else {
				setIsTyping(true);
				setProgressMessage('');
				setProgressPercent(null);
				setExportId('');
			}
		};

		const handleCancelExportJob = (data: {
			message: string;
			sessionId?: string;
		}) => {
			const sid = sessionIdRef.current;
			if (data.sessionId && sid && data.sessionId !== sid) {
				return;
			}
			stopElapsedTimer();
			setIsTyping(false);
			setProgressMessage(data.message);
			setProgressPercent(null);
			setExportId('');
		};

		const handleError = (error: { message: string }) => {
			setIsTyping(false);
			setError(error.message);
		};

		ChatGateway.socket.onConnect(handleConnect);
		ChatGateway.socket.onDisconnect(handleDisconnect);
		ChatGateway.socket.onMessage(handleResponse);
		ChatGateway.socket.onProgress(handleProgress);
		ChatGateway.socket.onCancell(handleCancelExportJob);
		ChatGateway.socket.onError(handleError);

		// Check initial connection state
		if (ChatGateway.socket.isConnected) {
			setIsConnected(true);
		}

		return () => {
			// Pause interval only — keep sessionStorage so timer resumes on re-mount
			if (elapsedIntervalRef.current) {
				clearInterval(elapsedIntervalRef.current);
				elapsedIntervalRef.current = null;
			}
			ChatGateway.socket.removeListener('connect', handleConnect);
			ChatGateway.socket.removeListener('disconnect', handleDisconnect);
			ChatGateway.socket.removeListener('chat:response', handleResponse);
			ChatGateway.socket.removeListener('chat:error', handleError);
			ChatGateway.socket.removeListener('export:progress', handleProgress);
			ChatGateway.socket.removeListener('export:cancelled', handleCancelExportJob);
			ChatGateway.socket.disconnect();
		};
	}, [user?.token, stopElapsedTimer, navigate]);

	// Re-subscribe when session changes while socket is already connected
	useEffect(() => {
		if (!user?.token || !sessionId || !ChatGateway.socket.isConnected) return;
		ChatGateway.socket.subscribeSession(sessionId, user.token);
	}, [user?.token, sessionId, isConnected]);

	const sendMessage = useCallback(
		(content: string) => {
			if (!content.trim()) return;

			const userMessage: ChatMessage = {
				id: generateId(),
				role: 'user',
				content,
				timestamp: new Date(),
			};

			setMessages((prev) => [...prev, userMessage]);
			setIsTyping(true);
			setError(null);
			setProgressMessage('');
			setProgressPercent(null);
			setExportId('');
			startElapsedTimer();

			// Send message with current sessionId (can be null for first message)
			ChatGateway.socket.sendMessage({
				sessionId,
				message: content,
				token: user?.token || '',
			});
		},
		[sessionId, user, startElapsedTimer]
	);

	const showTableReport = useCallback((reportId: string) => {
		setShowTableReport(reportId);
	}, []);

	const cancelExportJob = useCallback((exportId: string) => {
		void ChatGateway.api.cancelExportJob(exportId).catch((err) => {
			console.warn('[ChatBot] cancel export failed', err);
		});
	}, []);

	const uploadFraudControlCsv = useCallback(
		async (file: File) => {
			setIsFraudControlUploading(true);
			setIsTyping(true);
			setProgressMessage(chatUiStrings[inferChatUiLocale(messagesRef.current)].fraudControlProcessing);
			setProgressPercent(null);
			startElapsedTimer();
			try {
				const { sessionId: returnedSessionId } = await ChatGateway.api.fraudControlCheck(
					file,
					sessionIdRef.current,
				);
				// Subscribe to the new session if one was created by the backend.
				// Do NOT navigate here — handleResponse will navigate when the result arrives,
				// which prevents the component from re-rendering and clearing progress state.
				if (returnedSessionId && returnedSessionId !== sessionIdRef.current) {
					setSessionId(returnedSessionId);
					localStorage.setItem('chat_session_id', returnedSessionId);
					if (user?.token) {
						ChatGateway.socket.subscribeSession(returnedSessionId, user.token);
					}
				}
			} catch {
				stopElapsedTimer();
				setIsTyping(false);
				setProgressMessage('');
				const errMsg: ChatMessage = {
					id: generateId(),
					role: 'assistant',
					content: chatUiStrings[inferChatUiLocale(messagesRef.current)].fraudControlError,
					timestamp: new Date(),
				};
				setMessages((prev) => [...prev, errMsg]);
			} finally {
				setIsFraudControlUploading(false);
			}
		},
		[user, startElapsedTimer, stopElapsedTimer],
	);

	const chatUi = useMemo(
		() => chatUiStrings[inferChatUiLocale(messages)],
		[messages]
	);

	const loadSession = useCallback(
		async (sessionIdToLoad: string) => {
			try {
				setIsTyping(true);
				setError(null);
				const data = await ChatGateway.api.getSessionMessages(sessionIdToLoad);

				// Convert backend messages to ChatMessage format
				const chatMessages: ChatMessage[] = data.messages
					.filter((msg: any) => msg.role === 'user' || msg.role === 'assistant')
					.map((msg: any) => ({
						id: msg.id,
						role: msg.role,
						content: msg.content,
						timestamp: new Date(msg.createdAt),
					}));

				messagesRef.current = chatMessages;
				setMessages(chatMessages);
				applyActiveExportFromResponse(data.activeExport ?? null);
				if (data.activeExport?.exportId) {
					const storedStart = sessionStorage.getItem('chat_elapsed_start');
					if (storedStart) startElapsedTimer(parseInt(storedStart, 10));
				}
				setSessionId(sessionIdToLoad);
				localStorage.setItem('chat_session_id', sessionIdToLoad);
				// Navigate to session URL
				navigate(`/chatbot/sessionId/${sessionIdToLoad}`, { replace: true });
				setIsTyping(false);
			} catch (error) {
				console.error('[ChatBot] Error loading session messages:', error);
				setError('Failed to load session messages');
				setIsTyping(false);
			}
		},
		[navigate, applyActiveExportFromResponse, startElapsedTimer]
	);

	return {
		messages,
		isTyping,
		error,
		isConnected,
		sendMessage,
		clearConversation,
		showTableReport,
		tableReportId,
		progressMessage,
		progressPercent,
		exportId,
		cancelExportJob,
		loadSession,
		sessionId,
		chatUi,
		elapsedSeconds,
		uploadFraudControlCsv,
		isFraudControlUploading,
	};
}
